/**
 * useWaitlistGate(slug) — generic Peitho waitlist pre-flight gate.
 * Fail-open on network/config errors (Cerberus is the real auth boundary;
 * this hook only saves waitlisted users from confusing "wrong password"
 * loops). See test file for the contract.
 */
import { useCallback } from 'react'
import { getRuntimeConfig } from '@/lib/runtime-config'

interface WaitlistGateCheckResult {
  allowed: boolean
  error: string | null
}
interface WaitlistGateResult {
  check: (email: string) => Promise<WaitlistGateCheckResult>
}

export async function checkWaitlistAccess(
  domainSlug: string,
  email: string,
  opts: { fetchImpl?: typeof fetch; timeoutMs?: number } = {},
): Promise<WaitlistGateCheckResult> {
  const f = opts.fetchImpl ?? fetch
  const open = (m: string) => ({ allowed: true, error: m })
  let base: string
  try {
    const cfg = await getRuntimeConfig()
    if (!cfg.api.peitho) return open('no-peitho')
    base = cfg.api.peitho.replace(/\/$/, '')
  } catch (e) {
    return open(e instanceof Error ? e.message : 'cfg')
  }
  const u = `${base}/v1/waitlist/can-access?domain_slug=${encodeURIComponent(domainSlug)}&email=${encodeURIComponent(email)}`
  const c = new AbortController()
  const t = setTimeout(() => c.abort(), opts.timeoutMs ?? 4000)
  try {
    const r = await f(u, { signal: c.signal })
    if (!r.ok) return open(`http-${r.status}`)
    const b = (await r.json()) as { data?: { allowed?: unknown } }
    const a = b?.data?.allowed
    return typeof a === 'boolean' ? { allowed: a, error: null } : open('malformed')
  } catch (e) {
    return open(e instanceof Error ? e.message : 'net')
  } finally {
    clearTimeout(t)
  }
}

export function useWaitlistGate(domainSlug: string): WaitlistGateResult {
  return {
    check: useCallback(
      (email: string) => checkWaitlistAccess(domainSlug, email),
      [domainSlug],
    ),
  }
}
